import { useState, useEffect, useRef } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'
import { 
    Monitor, 
    ChevronDown, 
    ChevronUp,
    Plus,
    Edit3,
    Play,
    Pause,
    Settings,
    Grid,
    Camera,
    Clock,
    MoreHorizontal
} from 'lucide-react'

const ScreenThumbnailSelector = ({ 
    onScreenChange, 
    onEditScreen, 
    onOpenScreenManager,
    availableCameras = [],
    isFullscreen = false,
    showAutoRotationControls = true
}) => {
    const {
        preferences,
        setActiveScreen,
        getScreens,
        getMostUsedScreens,
        updateAutoRotation
    } = usePreferences()

    const [isOpen, setIsOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('recent') // 'recent', 'all', 'favorites'
    const dropdownRef = useRef(null)

    const screens = getScreens()
    const activeScreenId = preferences.screens?.activeScreenId
    const activeScreen = activeScreenId ? preferences.screens?.screenConfigs?.[activeScreenId] : null
    const autoRotation = preferences.screens?.autoRotation
    const recentScreens = preferences.screens?.lastUsedScreens?.map(id => 
        preferences.screens?.screenConfigs?.[id]
    ).filter(Boolean) || []
    const mostUsedScreens = getMostUsedScreens(5)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleScreenSelect = (screenId) => {
        setActiveScreen(screenId)
        setIsOpen(false)
        if (onScreenChange) {
            onScreenChange(screenId)
        }
    }

    const toggleAutoRotation = (e) => {
        e.stopPropagation()
        updateAutoRotation({ enabled: !autoRotation.enabled })
    }

    const getCameraById = (cameraId) => {
        return availableCameras.find(c => c._id === cameraId)
    }

    const getScreensForTab = () => {
        switch (activeTab) {
            case 'recent':
                return recentScreens.slice(0, 8)
            case 'favorites':
                return mostUsedScreens
            case 'all':
            default:
                return screens
        }
    }

    const screenList = getScreensForTab()

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Current Screen Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    isFullscreen ? 'text-gray-200 bg-gray-800/80 border-gray-600 hover:bg-gray-700/80' : ''
                }`}
            >
                <Monitor className="w-4 h-4" />
                <div className="text-left min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                        {activeScreen ? activeScreen.name : 'No Screen Selected'}
                    </div>
                    {activeScreen && (
                        <div className={`text-xs ${isFullscreen ? 'text-gray-300' : 'text-gray-500'}`}>
                            {activeScreen.grid.columns}×{activeScreen.grid.rows} • {activeScreen.cameras.length} cameras
                        </div>
                    )}
                </div>
                {autoRotation.enabled && showAutoRotationControls && (
                    <div className="flex items-center gap-1">
                        <Play className="w-3 h-3 text-green-500" />
                        <span className="text-xs text-green-500">{autoRotation.interval}s</span>
                    </div>
                )}
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className={`absolute top-full left-0 mt-2 w-96 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden ${
                    isFullscreen ? 'bg-gray-800/95 backdrop-blur-sm border-gray-600' : ''
                }`}>
                    {/* Header with Tabs */}
                    <div className="p-3 border-b dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className={`font-semibold ${isFullscreen ? 'text-gray-200' : 'text-gray-900 dark:text-white'}`}>
                                Select Screen
                            </h3>
                            <div className="flex items-center gap-1">
                                {showAutoRotationControls && (
                                    <button
                                        onClick={toggleAutoRotation}
                                        className={`p-1.5 rounded-lg ${
                                            autoRotation.enabled 
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                        }`}
                                        title={`Auto-rotation ${autoRotation.enabled ? 'enabled' : 'disabled'}`}
                                    >
                                        {autoRotation.enabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setIsOpen(false)
                                        onOpenScreenManager?.()
                                    }}
                                    className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                                    title="Manage screens"
                                >
                                    <Settings className="w-3 h-3" />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                            <button
                                onClick={() => setActiveTab('recent')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    activeTab === 'recent'
                                        ? isFullscreen 
                                            ? 'bg-gray-700 text-gray-200 shadow-sm'
                                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                        : isFullscreen
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Recent
                            </button>
                            <button
                                onClick={() => setActiveTab('favorites')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    activeTab === 'favorites'
                                        ? isFullscreen 
                                            ? 'bg-gray-700 text-gray-200 shadow-sm'
                                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                        : isFullscreen
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Popular
                            </button>
                            <button
                                onClick={() => setActiveTab('all')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    activeTab === 'all'
                                        ? isFullscreen 
                                            ? 'bg-gray-700 text-gray-200 shadow-sm'
                                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                        : isFullscreen
                                            ? 'text-gray-400 hover:text-gray-200'
                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                All ({screens.length})
                            </button>
                        </div>
                    </div>

                    {/* Screen List */}
                    <div className="max-h-64 overflow-y-auto">
                        {screenList.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                                <Grid className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No screens available</p>
                                <button
                                    onClick={() => {
                                        setIsOpen(false)
                                        onOpenScreenManager?.()
                                    }}
                                    className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Create your first screen
                                </button>
                            </div>
                        ) : (
                            <div className="p-2 space-y-1">
                                {screenList.map(screen => (
                                    <ScreenThumbnail
                                        key={screen.id}
                                        screen={screen}
                                        isActive={activeScreenId === screen.id}
                                        onSelect={() => handleScreenSelect(screen.id)}
                                        onEdit={() => {
                                            setIsOpen(false)
                                            onEditScreen?.(screen.id)
                                        }}
                                        availableCameras={availableCameras}
                                        getCameraById={getCameraById}
                                        isFullscreen={isFullscreen}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                        <button
                            onClick={() => {
                                setIsOpen(false)
                                onOpenScreenManager?.()
                            }}
                            className={`w-full flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors ${
                                isFullscreen 
                                    ? 'text-blue-400 hover:bg-blue-900/20' 
                                    : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            }`}
                        >
                            <Plus className="w-4 h-4" />
                            Create New Screen
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// Individual Screen Thumbnail Component
const ScreenThumbnail = ({ 
    screen, 
    isActive, 
    onSelect, 
    onEdit, 
    availableCameras, 
    getCameraById,
    isFullscreen = false
}) => {
    const [showMenu, setShowMenu] = useState(false)

    return (
        <div className={`relative group p-3 rounded-lg cursor-pointer transition-all ${
            isActive 
                ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' 
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}>
            <div onClick={onSelect} className="flex items-center gap-3">
                {/* Screen Preview */}
                <div className="w-16 h-10 bg-gray-100 dark:bg-gray-600 rounded border overflow-hidden flex-shrink-0">
                    <div 
                        className="w-full h-full grid gap-px p-1"
                        style={{
                            gridTemplateColumns: `repeat(${screen.grid.columns}, 1fr)`,
                            gridTemplateRows: `repeat(${screen.grid.rows}, 1fr)`,
                            backgroundColor: screen.grid.backgroundColor || '#1a1a1a'
                        }}
                    >
                        {Array.from({ length: screen.grid.columns * screen.grid.rows }).map((_, index) => {
                            const camera = screen.cameras[index]
                            const hasCamera = camera && getCameraById(camera.cameraId)
                            return (
                                <div
                                    key={index}
                                    className={`rounded-sm ${
                                        hasCamera 
                                            ? 'bg-blue-400 dark:bg-blue-500' 
                                            : 'bg-gray-300 dark:bg-gray-500'
                                    }`}
                                />
                            )
                        })}
                    </div>
                </div>

                {/* Screen Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h4 className={`font-medium truncate ${isFullscreen ? 'text-gray-200' : 'text-gray-900 dark:text-white'}`}>
                            {screen.name}
                        </h4>
                        {isActive && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                                Active
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                        <div className={`text-xs flex items-center gap-1 ${isFullscreen ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                            <Grid className="w-3 h-3" />
                            {screen.grid.columns}×{screen.grid.rows}
                        </div>
                        <div className={`text-xs flex items-center gap-1 ${isFullscreen ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                            <Camera className="w-3 h-3" />
                            {screen.cameras.length}
                        </div>
                        {screen.usageCount > 0 && (
                            <div className={`text-xs flex items-center gap-1 ${isFullscreen ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                                <Clock className="w-3 h-3" />
                                {screen.usageCount}
                            </div>
                        )}
                    </div>
                    {screen.cameras.length > 0 && (
                        <div className={`mt-1 text-xs truncate ${isFullscreen ? 'text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {screen.cameras.slice(0, 2).map(cam => {
                                const cameraData = getCameraById(cam.cameraId)
                                return cameraData?.name
                            }).filter(Boolean).join(', ')}
                            {screen.cameras.length > 2 && ` +${screen.cameras.length - 2} more`}
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="relative">
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            setShowMenu(!showMenu)
                        }}
                        className={`p-1 rounded transition-colors ${
                            isFullscreen 
                                ? 'text-gray-400 hover:text-gray-200' 
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                    >
                        <MoreHorizontal className="w-4 h-4" />
                    </button>
                    
                    {showMenu && (
                        <div className={`absolute right-0 top-6 border rounded-lg shadow-lg z-10 min-w-[100px] ${
                            isFullscreen 
                                ? 'bg-gray-700 border-gray-600' 
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                        }`}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onEdit()
                                    setShowMenu(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                            >
                                <Edit3 className="w-3 h-3" />
                                Edit
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ScreenThumbnailSelector